import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
// import { Postdata } from "../../helpers/api.service";
import HttpService from "../../helpers/httpServices";
import { useCountryNavigate } from "../common/UseCountryNavigate";
import axios from "axios";

const ColorManagement = () => {
  const location = useLocation();
  const navigate = useCountryNavigate();

  const [data, setData] = useState({
    primary_color: "#5E35B1", // Default colors
    secondary_color: "#C04621", 
  });
  const [originalData, setOriginalData] = useState({
    primary_color: "#5E35B1",
    secondary_color: "#C04621",
  });
  const [errors, setErrors] = useState({});
  const [loader, setLoader] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(true);

  // Fetch existing colors on component mount
  useEffect(() => {
    fetchColors();
  }, []);

  const fetchColors = async () => {
    try {
      setFetchLoader(true);

      const res = await axios.get(HttpService.get_school_colors);

      if (res.status === 200 && res.data?.data) {
        const colors = {
          primary_color: res.data.data.primary_color || "#5E35B1",
          secondary_color: res.data.data.secondary_color || "#C04621",
        };
        setData(colors);
        setOriginalData(colors);
      }
    } catch (err) {
      console.error("Error loading colors:", err);
      toast.error("Failed to load existing colors");
    } finally {
      setFetchLoader(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    if (!data.primary_color) {
      newErrors.primary_color = "Primary color is required.";
    } else if (!hexColorRegex.test(data.primary_color)) {
      newErrors.primary_color =
        "Please enter a valid hex color (e.g., #1E40AF).";
    }

    if (!data.secondary_color) {
      newErrors.secondary_color = "Secondary color is required.";
    } else if (!hexColorRegex.test(data.secondary_color)) {
      newErrors.secondary_color =
        "Please enter a valid hex color (e.g., #F59E0B).";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validate()) return;

  if (
    data.primary_color === originalData.primary_color &&
    data.secondary_color === originalData.secondary_color
  ) {
    toast.info("No changes detected");
    return;
  }

  setLoader(true);

  try {
    const formData = new FormData();
    formData.append("primary_color", data.primary_color);
    formData.append("secondary_color", data.secondary_color);

    const res = await axios.post(HttpService.update_school_colors, formData);

    if (res.status === 200) {
      setOriginalData(data);
      toast.success(res.data?.message || "Colors updated successfully!");
    }
  } catch (err) {
    console.error("Error updating colors:", err);
    toast.error("Failed to update colors");
  } finally {
    setLoader(false);
  }
};


  const handleReset = () => {
    setData(originalData);
    setErrors({});
    toast.info("Colors reset to saved values");
  };

  const handleColorChange = (field, value) => {
    setData({ ...data, [field]: value });
    // Clear error for this field when user makes changes
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  useEffect(() => {
    // Navigate to /settings with currentTab only once
    if (location?.state?.currentTab !== "Color Management") {
      navigate("settings", {
        state: { currentTab: "Color Management" },
        replace: true,
      });
    }
  }, []);

  if (fetchLoader) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-bold text-[#000000]">Color Management</h2>
      <div className="p-6 space-y-6 pr-0">
        <p className="text-gray-600 mb-4">
          Customize your school's brand colors. These colors will be applied
          across the application.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Primary Color Picker */}
            <div>
              <label className="block mb-2">
                Primary Color<span className="required text-red-500">*</span>
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <input
                      type="color"
                      value={data.primary_color}
                      onChange={(e) =>
                        handleColorChange("primary_color", e.target.value)
                      }
                      className="w-20 h-20 rounded-lg cursor-pointer border-2 border-gray-300"
                      style={{ padding: "4px" }}
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={data.primary_color}
                      onChange={(e) =>
                        handleColorChange("primary_color", e.target.value)
                      }
                      placeholder="#1E40AF"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary_main uppercase"
                      maxLength={7}
                    />
                  </div>
                </div>
                {errors.primary_color && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.primary_color}
                  </p>
                )}

                {/* Preview Box for Primary Color */}
                <div className="mt-3">
                  <p className="text-sm text-gray-600 mb-2">Preview:</p>
                  <div
                    className="w-full h-16 rounded-lg border-2 border-gray-300 flex items-center justify-center text-white font-semibold"
                    style={{ backgroundColor: data.primary_color }}
                  >
                    Primary Color Preview
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Color Picker */}
            <div>
              <label className="block mb-2">
                Secondary Color<span className="required text-red-500">*</span>
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <input
                      type="color"
                      value={data.secondary_color}
                      onChange={(e) =>
                        handleColorChange("secondary_color", e.target.value)
                      }
                      className="w-20 h-20 rounded-lg cursor-pointer border-2 border-gray-300"
                      style={{ padding: "4px" }}
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={data.secondary_color}
                      onChange={(e) =>
                        handleColorChange("secondary_color", e.target.value)
                      }
                      placeholder="#F59E0B"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary_main uppercase"
                      maxLength={7}
                    />
                  </div>
                </div>
                {errors.secondary_color && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.secondary_color}
                  </p>
                )}

                {/* Preview Box for Secondary Color */}
                <div className="mt-3">
                  <p className="text-sm text-gray-600 mb-2">Preview:</p>
                  <div
                    className="w-full h-16 rounded-lg border-2 border-gray-300 flex items-center justify-center text-white font-semibold"
                    style={{ backgroundColor: data.secondary_color }}
                  >
                    Secondary Color Preview
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="w-full flex justify-center gap-3 mb-2 mt-6">
            <button
              type="submit"
              className="button-field-primary px-8 py-2 rounded-lg transition-colors"
              style={{ width: "30%" }}
              disabled={loader}
            >
              {loader ? "Saving..." : "Save Colors"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ColorManagement;
